import {
  revokeInvite,
  sendInviteEmails,
  updateCadFileInvite,
} from '@api/cadFile.js';
import {
  revokeFolderInvite,
  shareFolder,
  updateFolderInviteAccess,
} from '@api/projects.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import { DropdownIcon, XCircleIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIImage, UIInput, UISpan, UIText } from '@ui';
import { validateEmail } from '@utils/validation.js';
import { S3URL } from '/config.js';

export default class InvitedList {
  constructor(options = {}) {
    this.accessControl = options.accessControl || false;
    this.orgUsers = options.orgUsers || [];
    this.emails = options.emails || [];
    this.isOrgUsersVisible = options.isOrgUsersVisible || false;
    this.currentUserId = options.currentUserId;
    this.resourceOwnerId = options.resourceOwnerId;
    this.resourceId = options.resourceId || '';
    this.onEmailsChange = options.onEmailsChange || (() => {});
    this.hasOrg = options.hasOrg || false;
    this.isProject = options.isProject || false;
    this.title = options.title || i18next.t('menubar.share.who_access');
    this.container = null;
  }

  render() {
    const wrapperDiv = new UIDiv();
    wrapperDiv.setClass('mb-6');

    const titleDiv = new UIText(this.title);
    titleDiv.setClass('font-medium mb-2 block');

    const container = new UIDiv();
    container.setClass(
      'bg-white border border-gray-200 rounded-xl p-2 max-h-[220px] overflow-y-auto overscroll-contain',
    );
    this.container = container;

    const confirmedEmails = this.emails.filter(email => !email.localStored);

    if (this.hasOrg && this.resourceOwnerId) {
      const ownerUser = this.orgUsers.find(
        user => user.uid === this.resourceOwnerId,
      );
      if (ownerUser) {
        const ownerItem = this.createUserItem({
          user: ownerUser,
          isFixedOwner: true,
        });
        container.add(ownerItem);
      }
    }

    const allUsers = this.isOrgUsersVisible
      ? this.mergeUsersAndInvites(confirmedEmails)
      : confirmedEmails;

    if (allUsers.length === 0 && (!this.hasOrg || !this.resourceOwnerId)) {
      const noUsersDiv = new UIDiv();
      noUsersDiv.setClass('py-2');
      noUsersDiv.setTextContent('No users or invited emails found.');
      container.add(noUsersDiv);
    } else {
      allUsers.forEach(user => {
        if (user.user?.uid === this.resourceOwnerId) return;
        const userItem = this.createUserItem(user);
        container.add(userItem);
      });
    }

    wrapperDiv.add(titleDiv, container);
    return wrapperDiv;
  }

  createUserItem(user) {
    const userDiv = new UIDiv();
    userDiv.setClass(
      'flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 py-2 rounded-md opacity-0 hover:bg-gray-100',
    );

    if (user.isFixedOwner) {
      userDiv.addClass('border-b border-gray-200');
    }

    const userEmail = user.user ? user.user.email : user.email;
    userDiv.dom.setAttribute('data-email', userEmail);
    userDiv.dom.setAttribute('data-user-item', 'true');
    if (user.isFixedOwner) {
      userDiv.dom.setAttribute('data-fixed-owner', 'true');
    }

    const userWrapper = new UIDiv();
    userWrapper.setClass(
      'user-wrapper flex items-center w-full sm:w-auto mb-2 sm:mb-0',
    );

    const avatar = this.createUserAvatar(user);

    const nameEmailDiv = new UIDiv();
    nameEmailDiv.setClass(
      'name-email max-w-[200px] sm:max-w-[150px] md:max-w-[300px] truncate ml-3',
    );

    const nameSpan = new UISpan();
    nameSpan.setClass('user-name block font-medium');
    nameSpan.setTextContent(this.getUserDisplayName(user));

    const emailSpan = new UISpan();
    emailSpan.setClass('user-email block text-sm text-gray-500');
    emailSpan.setTextContent(user.user ? user.user.email : user.email);

    nameEmailDiv.add(nameSpan, emailSpan);
    userWrapper.add(avatar, nameEmailDiv);

    if (
      !user.isFixedOwner &&
      !this.isOwner(user) &&
      !this.isOrgAdmin(user) &&
      user.user?.uid !== this.currentUserId
    ) {
      const makeEditable = () => {
        const editInput = new UIInput(userEmail);
        editInput.setClass(
          'ml-3 bg-transparent border border-gray-300 rounded px-2 py-1 font-medium w-[200px] md:w-[300px] h-[40px]',
        );
        userWrapper.dom.replaceChild(editInput.dom, nameEmailDiv.dom);
        editInput.dom.focus();
        editInput.dom.select();
        return editInput;
      };

      const revertEditable = input => {
        if (userWrapper.contains(input.dom)) {
          userWrapper.dom.replaceChild(nameEmailDiv.dom, input.dom);
        }
      };

      const updateEmail = async newValue => {
        if (!newValue || newValue === userEmail || !validateEmail(newValue)) {
          toast.error(i18next.t('menubar.share.toast.invalid_email'));
          return false;
        }

        try {
          const currentRole = user.accessLevel?.level || 'View';
          const newInvite = {
            email: newValue,
            accessLevel: { level: currentRole },
            expiry: new Date(Date.now() + 24 * 60 * 60 * 1000),
          };

          if (this.isProject) {
            await shareFolder(this.resourceId, {
              invitationDetails: [
                {
                  recipient: newValue,
                  accessLevel: currentRole,
                },
              ],
            });
          } else {
            await sendInviteEmails(this.resourceId, [newInvite]);
          }

          await this.revokeUserAccess(user, true);
          this.emails.push(newInvite);
          this.onEmailsChange(this.emails);

          const newUserItem = this.createUserItem({
            email: newValue,
            accessLevel: { level: currentRole },
          });
          this.container.add(newUserItem);

          toast.success(i18next.t('menubar.share.toast.email_updated'));
          return true;
        } catch (error) {
          console.error('Failed to update email:', error);
          toast.error(i18next.t('menubar.share.toast.email_update_failed'));
          return false;
        }
      };

      let isEditing = false;
      nameEmailDiv.dom.addEventListener('dblclick', () => {
        if (isEditing) return;
        if (user.isFixedOwner) return;
        if (user.user?.uid === this.currentUserId) return;
        if (
          user.accessLevel?.level === 'Edit' &&
          !this.accessControl.hasPermission('invite_admin')
        )
          return;
        if (
          user.accessLevel?.level === 'Manager' &&
          !this.accessControl.hasPermission('invite_manager')
        )
          return;
        if (
          user.accessLevel?.level === 'Member' &&
          !this.accessControl.hasPermission('invite_member')
        )
          return;
        isEditing = true;

        const input = makeEditable();
        const finishEditing = async newValue => {
          if (!isEditing) return;
          isEditing = false;
          if (!(await updateEmail(newValue?.trim()))) {
            revertEditable(input);
          }
        };

        input.onKeyDown(e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finishEditing(input.getValue());
          } else if (e.key === 'Escape') {
            finishEditing(null);
          }
        });

        input.onBlur(() =>
          setTimeout(() => finishEditing(input.getValue()), 100),
        );
      });
    }

    const roleDiv = new UIDiv();
    roleDiv.setClass('user-role w-full sm:w-auto');

    if (user.isFixedOwner || this.isOwner(user)) {
      const ownerText = new UISpan();
      ownerText.setTextContent(i18next.t('role.owner'));
      ownerText.setClass('user-role-text text-gray-500 text-sm font-medium');
      roleDiv.add(ownerText);
    } else if (this.isOrgAdmin(user)) {
      const adminText = new UISpan();
      adminText.setTextContent(i18next.t('role.admin'));
      adminText.setClass('user-role-text text-sm font-medium');
      roleDiv.add(adminText);
    } else {
      const roleSelect = this.createRoleSelect(user);
      roleDiv.add(roleSelect);
    }

    userDiv.add(userWrapper, roleDiv);

    requestAnimationFrame(() => {
      userDiv.dom.classList.remove('opacity-0', '-translate-y-2');
    });

    return userDiv;
  }

  createUserAvatar(user) {
    if (user?.user?.profileImageKey) {
      const userImage = new UIImage(`${S3URL}${user.user.profileImageKey}`);
      userImage.setClass('user-image w-8 h-8 rounded-full object-cover');
      return userImage;
    }

    const placeholderDiv = new UIDiv();
    placeholderDiv.setClass(
      'user-placeholder w-8 h-8 rounded-full bg-[var(--o-hive-primary-100)] flex items-center justify-center text-white font-bold',
    );
    const displayName = user.user ? user.user.full_name : user.email;
    placeholderDiv.setTextContent(
      displayName ? displayName[0].toUpperCase() : '?',
    );
    return placeholderDiv;
  }

  createRoleSelect(user) {
    const roleButton = new UIButton();
    roleButton.setClass(
      'flex items-center gap-2 bg-gray-100 hover:bg-gray-200 py-1 px-3 rounded transition-all duration-300 text-sm w-full justify-center sm:w-auto sm:justify-between sm:justify-start',
    );

    const currentRole = user.accessLevel?.level || user.role || 'View';
    const roleText = new UIText(
      i18next.t(`menubar.share.option_${currentRole.toLowerCase()}`),
    );
    roleText.setClass('role-text');

    const dropdownArrow = new DropdownIcon({
      size: 10,
      color: 'black',
    });
    if (
      (user.user && user.user.uid === this.currentUserId) ||
      (user.accessLevel?.level === 'Edit' &&
        !this.accessControl.hasPermission('invite_admin')) ||
      (user.accessLevel?.level === 'Manager' &&
        !this.accessControl.hasPermission('invite_manager'))
    ) {
      roleButton.setDisabled(true);
      roleButton.add(roleText);
      return roleButton;
    }
    roleButton.add(roleText, dropdownArrow);

    const popoverContent = new UIDiv();

    const updatePopoverContent = () => {
      popoverContent.dom.innerHTML = '';
      const roles = [];

      if (this.accessControl.hasPermission('invite_admin')) roles.push('Edit');
      if (this.accessControl.hasPermission('invite_manager'))
        roles.push('Manager');
      if (this.accessControl.hasPermission('invite_member')) roles.push('View');
      roles.forEach(role => {
        const option = new UIButton();
        option.setClass(
          'w-full rounded-md text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between',
        );

        const optionText = new UIText(
          i18next.t(`menubar.share.option_${role.toLowerCase()}`),
        );
        const checkmark = new UIDiv();

        if (user.accessLevel?.level === role) {
          checkmark.setClass('w-4 h-4 text-[var(--o-hive-primary-100)]');
          checkmark.setTextContent('✓');
        }

        option.add(optionText, checkmark);
        option.onClick(() => {
          this.updateUserRole(user, role);
          roleText.setTextContent(
            i18next.t(`menubar.share.option_${role.toLowerCase()}`),
          );
          user.accessLevel = { level: role };
          updatePopoverContent();
          Popover.hide();
        });

        popoverContent.add(option);
      });

      if (this.accessControl.hasPermission('update')) {
        const separator = new UIDiv();
        separator.setClass('h-[1px] bg-gray-200 my-1');
        popoverContent.add(separator);

        const revokeOption = new UIButton();
        revokeOption.setClass(
          'w-full rounded-md text-left px-4 py-2 gap-2 hover:bg-gray-100 flex items-center text-red-600',
        );

        const revokeIcon = new XCircleIcon({
          size: 16,
          color: 'none',
        });

        const revokeText = new UIText(i18next.t('menubar.share.revoke'));
        revokeOption.add(revokeIcon, revokeText);
        revokeOption.onClick(() => {
          this.revokeUserAccess(user);
          Popover.hide();
        });

        popoverContent.add(revokeOption);
      }
    };

    updatePopoverContent();

    roleButton.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: roleButton,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: false,
        hideOnScroll: true,
      });
    });

    return roleButton;
  }

  getUserDisplayName(user) {
    if (user.user) {
      return user.user.full_name || 'Unknown User';
    }
    return user.firstName
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : user.email;
  }

  isOwner(user) {
    return (
      user.uid === this.resourceOwnerId ||
      (user.user && user.user.uid === this.resourceOwnerId)
    );
  }

  isOrgAdmin(user) {
    return this.orgUsers.some(
      orgUser =>
        orgUser &&
        user.user &&
        orgUser.email === user.user.email &&
        orgUser.role === 'admin',
    );
  }

  async revokeUserAccess(user, silent = false) {
    const emailToRevoke = user.email || user.user?.email;
    const userItem = document.querySelector(`[data-email="${emailToRevoke}"]`);

    try {
      if (userItem) {
        userItem.classList.add('opacity-0', 'translate-y-2');
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (this.isProject) {
        await revokeFolderInvite(this.resourceId, {
          email: emailToRevoke,
        });
      } else {
        await revokeInvite(emailToRevoke, this.resourceId);
      }

      this.emails = this.emails.filter(email => email.email !== emailToRevoke);
      this.onEmailsChange(this.emails);

      if (userItem?.parentNode) {
        userItem.parentNode.removeChild(userItem);

        if (
          this.container &&
          !this.container.dom.querySelector('[data-user-item]')
        ) {
          const noUsersDiv = new UIDiv();
          noUsersDiv.setClass('py-2');
          noUsersDiv.setTextContent('No users or invited emails found.');
          this.container.add(noUsersDiv);
        }
      }

      if (!silent) {
        toast.success(
          i18next.t('menubar.share.toast.revoke_access_success', {
            email: emailToRevoke,
          }),
        );
      }
    } catch (error) {
      console.error('Failed to revoke access:', error);
      if (!silent) {
        toast.error(
          i18next.t('menubar.share.toast.revoke_access_failed', {
            email: emailToRevoke,
          }),
        );
      }
      if (userItem) userItem.classList.remove('opacity-0', 'translate-y-2');
      throw error;
    }
  }

  async updateUserRole(user, newRole) {
    try {
      if (user.user) {
        const orgUserIndex = this.orgUsers.findIndex(
          orgUser => orgUser.email === user.user.email,
        );

        if (orgUserIndex !== -1) {
          if (!this.orgUsers[orgUserIndex].accessLevel) {
            this.orgUsers[orgUserIndex].accessLevel = {};
          }
          this.orgUsers[orgUserIndex].accessLevel.level = newRole;
        }

        let existingInvite = this.emails.find(
          invite => invite.email === user.user.email,
        );

        if (!existingInvite) {
          existingInvite = {
            email: user.user.email,
            accessLevel: { level: newRole },
            expiry: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
          };

          this.emails.push(existingInvite);

          if (this.container) {
            const userItem = this.createUserItem({
              user: user.user,
              accessLevel: { level: newRole },
            });
            this.container.add(userItem);
          }

          if (this.isProject) {
            await shareFolder(this.resourceId, {
              invitationDetails: [
                {
                  recipient: user.user.email,
                  fullName: user.user.full_name,
                  accessLevel: newRole,
                },
              ],
            });
          } else {
            await sendInviteEmails(this.resourceId, [existingInvite]);
          }
        } else {
          if (typeof existingInvite.accessLevel === 'string') {
            existingInvite.accessLevel = { level: existingInvite.accessLevel };
          }
          existingInvite.accessLevel.level = newRole;

          this.updateRoleTextInDOM(user.user.email, newRole);

          if (this.isProject) {
            await updateFolderInviteAccess(this.resourceId, {
              email: user.user.email,
              accessLevel: newRole,
            });
          } else {
            await updateCadFileInvite(
              this.resourceId,
              user.user.email,
              newRole,
            );
          }
        }
      } else {
        user.accessLevel.level = newRole;

        this.updateRoleTextInDOM(user.email, newRole);

        if (this.isProject) {
          await updateFolderInviteAccess(this.resourceId, {
            email: user.email,
            accessLevel: newRole,
          });
        } else {
          await updateCadFileInvite(this.resourceId, user.email, newRole);
        }
      }

      this.onEmailsChange(this.emails);
      toast.success(i18next.t('menubar.share.toast.role_update_success'));
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(i18next.t('menubar.share.toast.role_update_failed'));
    }
  }

  updateRoleTextInDOM(email, newRole) {
    const userItem = document.querySelector(`[data-email="${email}"]`);
    if (userItem) {
      const roleText = userItem.querySelector('.role-text');
      if (roleText) {
        roleText.textContent = i18next.t(
          `menubar.share.option_${newRole.toLowerCase()}`,
        );
      }
    }
  }

  addUserItem(user) {
    if (!this.container) return;

    const noUsersMessage = this.container.dom.querySelector(
      ':scope > div:not([data-user-item])',
    );
    if (noUsersMessage) noUsersMessage.remove();

    this.container.add(this.createUserItem(user));
  }

  mergeUsersAndInvites(confirmedEmails) {
    if (!this.isOrgUsersVisible) {
      return confirmedEmails.map(email => ({
        email,
        accessLevel: { level: 'View' },
      }));
    }

    const invitedOrgUsers = this.orgUsers.filter(orgUser =>
      confirmedEmails.some(invite => invite.email === orgUser.email),
    );

    const mergedUsers = invitedOrgUsers.map(orgUser => {
      const existingInvite = confirmedEmails.find(
        invite => invite.email === orgUser.email,
      );
      return {
        ...orgUser,
        accessLevel: existingInvite
          ? { level: existingInvite.accessLevel.level }
          : { level: orgUser.role || 'View' },
      };
    });

    const additionalInvites = confirmedEmails.filter(
      invite => !this.orgUsers.some(orgUser => orgUser.email === invite.email),
    );

    return [...mergedUsers, ...additionalInvites];
  }
}
